<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useWhatsappEmbeddedSignup } from 'dashboard/composables/useWhatsappEmbeddedSignup';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'next/button/Button.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import globalConstants from 'dashboard/constants/globals.js';
import {
  setupFacebookSdk,
  initWhatsAppEmbeddedSignup,
  createMessageHandler,
} from './whatsapp/utils';
import SalvyApi from 'dashboard/api/salvy';
import ActionCable from '@rails/actioncable';

const props = defineProps({
  enableCallingOnComplete: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { isAuthenticating, runEmbeddedSignup } = useWhatsappEmbeddedSignup();

const isProcessing = ref(false);
const processingMessage = ref('');

// Salvy State
const showSalvyForm = ref(false);
const salvyAreaCodes = ref([]);
const selectedAreaCode = ref('');
const isProvisioningSalvy = ref(false);
const waitingForSms = ref(false);
const salvyPhoneNumber = ref('');
const salvyCable = ref(null);
const isFrictionlessFlow = ref(false);

const benefits = computed(() => [
  {
    key: 'EASY_SETUP',
    text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.EASY_SETUP'),
  },
  {
    key: 'SECURE_AUTH',
    text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.SECURE_AUTH'),
  },
  {
    key: 'AUTO_CONFIG',
    text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.AUTO_CONFIG'),
  },
]);

const showLoader = computed(() => isAuthenticating.value || isProcessing.value);

const enableCallingForInbox = async inboxId => {
  try {
    await InboxesAPI.enableWhatsappCalling(inboxId);
  } catch (error) {
    const response = error?.response?.data;
    if (response?.category === 'calling_eligibility') {
      useAlert(
        t('INBOX_MGMT.WHATSAPP_CALLING.ELIGIBILITY_FAILED', {
          code: response.code || '138015',
        })
      );
    } else {
      useAlert(
        response?.error ||
          t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CALLING_ENABLE_FAILED')
      );
    }
  }
};

const handleSignupSuccess = async inboxData => {
  if (inboxData && inboxData.id) {
    if (props.enableCallingOnComplete) {
      processingMessage.value = t(
        'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.ENABLING_CALLING'
      );
      await enableCallingForInbox(inboxData.id);
    }
    isProcessing.value = false;
    useAlert(t('INBOX_MGMT.FINISH.MESSAGE'));
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: inboxData.id,
      },
    });
  } else {
    isProcessing.value = false;
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUCCESS_FALLBACK'));
    router.replace({
      name: 'settings_inbox_list',
    });
  }
};

// Salvy frictionless flow: the number was provisioned via Salvy, so we only
// need WABA sharing from Meta (FINISH_ONLY_WABA) and then register the number
// in the background. The window listener is scoped to a single run, mirroring
// useWhatsappEmbeddedSignup.
const runSalvyFrictionlessSignup = () =>
  new Promise((resolve, reject) => {
    let settled = false;
    let messageHandler;

    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', messageHandler);
      fn(value);
    };

    messageHandler = createMessageHandler(data => {
      if (data.event === 'FINISH_ONLY_WABA') {
        if (data.data && data.data.waba_id) {
          settle(resolve, { wabaId: data.data.waba_id });
        } else {
          settle(
            reject,
            new Error('Missing WABA ID for background registration.')
          );
        }
      } else if (data.event === 'CANCEL') {
        settle(resolve, null);
      } else if (data.event === 'error') {
        settle(
          reject,
          new Error(
            data.error_message ||
              t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SIGNUP_ERROR')
          )
        );
      }
    });

    window.addEventListener('message', messageHandler);

    (async () => {
      try {
        await setupFacebookSdk(
          window.chatwootConfig?.whatsappAppId,
          window.chatwootConfig?.whatsappApiVersion
        );
        const configId =
          window.globalConfig?.WHATSAPP_BYPASS_CONFIGURATION_ID ||
          window.chatwootConfig?.whatsappConfigurationId;
        await initWhatsAppEmbeddedSignup(configId, 'only_waba_sharing');
      } catch (error) {
        if (error.message === 'Login cancelled') {
          settle(resolve, null);
        } else {
          settle(reject, error);
        }
      }
    })();
  });

const launchSalvySignup = async () => {
  isProcessing.value = true;
  processingMessage.value = t(
    'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING'
  );
  try {
    const result = await runSalvyFrictionlessSignup();
    if (!result) {
      isProcessing.value = false;
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
      return;
    }

    processingMessage.value =
      'Registering your Salvy number in the background...';
    await SalvyApi.registerNumber({
      waba_id: result.wabaId,
      phone_number: salvyPhoneNumber.value,
      cc: selectedAreaCode.value,
      display_name: 'AirysChat',
    });
    isProcessing.value = false;
    useAlert(
      'Number successfully submitted to Meta. Waiting for SMS verification code...'
    );
  } catch (error) {
    isProcessing.value = false;
    useAlert(
      error.message || t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SIGNUP_ERROR')
    );
  }
};

const launchEmbeddedSignup = async () => {
  if (isFrictionlessFlow.value) {
    await launchSalvySignup();
    return;
  }

  let credentials;
  try {
    credentials = await runEmbeddedSignup();
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SDK_LOAD_ERROR')
    );
    return;
  }

  // Resolves null when the user dismisses the Meta popup.
  if (!credentials) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
    return;
  }

  isProcessing.value = true;
  processingMessage.value = t(
    'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING'
  );
  try {
    const inboxData = await store.dispatch(
      'inboxes/createWhatsAppEmbeddedSignup',
      credentials
    );
    await handleSignupSuccess(inboxData);
  } catch (error) {
    isProcessing.value = false;
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
    );
  }
};

// Salvy Flow
const setupActionCable = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const cableUrl = `${protocol}//${window.location.host}/cable`;
  salvyCable.value = ActionCable.createConsumer(cableUrl);
  salvyCable.value.subscriptions.create('SalvySmsChannel', {
    received: data => {
      if (data.to && salvyPhoneNumber.value.includes(data.to)) {
        useAlert(`Salvy SMS Received: ${data.body}`);
        waitingForSms.value = false;
      }
    },
  });
};

const toggleSalvyForm = async () => {
  showSalvyForm.value = !showSalvyForm.value;
  if (showSalvyForm.value && salvyAreaCodes.value.length === 0) {
    try {
      const response = await SalvyApi.getAreaCodes();
      if (response.data && response.data.areaCodes) {
        salvyAreaCodes.value = response.data.areaCodes
          .filter(c => c.available)
          .map(c => c.areaCode);
      } else {
        salvyAreaCodes.value = [];
      }
    } catch (error) {
      useAlert('Failed to load area codes from Salvy.');
    }
  }
};

const provisionSalvyNumber = async () => {
  if (!selectedAreaCode.value) return;
  isProvisioningSalvy.value = true;
  try {
    const response = await SalvyApi.createVirtualNumber(selectedAreaCode.value);
    const newNumber =
      response.data.phoneNumber || response.data.data.phoneNumber;
    salvyPhoneNumber.value = newNumber.toString().startsWith('+')
      ? newNumber
      : `+${newNumber}`;
    showSalvyForm.value = false;
    isFrictionlessFlow.value = true;
    waitingForSms.value = true;
    useAlert(
      'Number provisioned successfully! Waiting for SMS verification code...'
    );
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to provision number.');
  } finally {
    isProvisioningSalvy.value = false;
  }
};

// Lifecycle
onMounted(() => {
  setupActionCable();
});

onBeforeUnmount(() => {
  if (salvyCable.value) {
    salvyCable.value.disconnect();
  }
});
</script>

<template>
  <div class="h-full">
    <!-- Salvy State Displays (Always visible if active) -->
    <div
      v-if="salvyPhoneNumber"
      class="p-3 mb-4 text-sm text-n-green-text bg-n-green-background rounded-md border border-n-green-border font-mono text-center"
    >
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.YOUR_SALVY_NUMBER') }}
      <strong>{{ salvyPhoneNumber }}</strong>
    </div>

    <div
      v-if="waitingForSms"
      class="p-3 mb-4 text-sm text-n-blue-text bg-n-blue-background rounded-md border border-n-blue-border"
    >
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.WAITING_FOR_SMS') }}
    </div>

    <LoadingState v-if="showLoader" :message="processingMessage" />

    <div v-else>
      <div class="flex flex-col items-start mb-6 text-start">
        <div class="flex justify-start mb-6">
          <div
            class="flex size-11 items-center justify-center rounded-full bg-n-alpha-2"
          >
            <Icon icon="i-woot-whatsapp" class="text-n-slate-10 size-6" />
          </div>
        </div>

        <h3 class="mb-2 text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.TITLE') }}
        </h3>
        <p class="text-sm leading-[24px] text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.DESC') }}
        </p>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <div
          v-for="benefit in benefits"
          :key="benefit.key"
          class="flex gap-2 items-center text-sm text-n-slate-11"
        >
          <Icon icon="i-lucide-check" class="text-n-slate-11 size-4" />
          {{ benefit.text }}
        </div>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <I18nT
          keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.TEXT"
          tag="span"
          class="text-sm text-n-slate-11"
        >
          <template #link>
            <a
              :href="globalConstants.WHATSAPP_EMBEDDED_SIGNUP_DOCS_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="underline text-n-brand"
            >
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.LINK_TEXT'
                )
              }}
            </a>
          </template>
        </I18nT>
      </div>

      <!-- Salvy Form Injection -->
      <div class="flex flex-col gap-2 mb-6 border-t border-n-weak pt-6 mt-4">
        <div class="flex justify-between items-center">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.EMBEDDED_LINK_TEXT') }}
          </h4>
          <a
            href="#"
            class="text-sm text-n-brand underline"
            @click.prevent="toggleSalvyForm"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.LINK_TEXT') }}
          </a>
        </div>

        <div
          v-if="showSalvyForm"
          class="p-4 mt-2 mb-4 bg-n-alpha-1 rounded-md border border-n-weak"
        >
          <h4 class="text-sm font-semibold text-n-slate-12 mb-2">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.FORM_TITLE') }}
          </h4>
          <p class="text-xs text-n-slate-11 mb-4">
            <strong>{{
              $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_LABEL')
            }}</strong>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_DESC') }}
            <strong>{{
              $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_PRICE')
            }}</strong>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_SUFFIX') }}
          </p>
          <div class="flex gap-2">
            <select
              v-model="selectedAreaCode"
              class="block w-full text-sm border border-n-weak rounded-md p-2"
            >
              <option value="" disabled>
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.AREA_CODE_PLACEHOLDER') }}
              </option>
              <option v-for="ddd in salvyAreaCodes" :key="ddd" :value="ddd">
                {{ ddd }}
              </option>
            </select>
            <NextButton
              :is-loading="isProvisioningSalvy"
              :disabled="!selectedAreaCode"
              solid
              blue
              :label="$t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BUY_BUTTON')"
              @click.prevent="provisionSalvyNumber"
            />
          </div>
        </div>
      </div>

      <div class="flex mt-4">
        <NextButton
          :disabled="isAuthenticating"
          :is-loading="isAuthenticating"
          faded
          slate
          class="w-full"
          @click="launchEmbeddedSignup"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUBMIT_BUTTON') }}
        </NextButton>
      </div>
    </div>
  </div>
</template>
